import { PostMessageSender, PostMessageHandlers } from '../Shared/PostMessage';
import { IFluidObject, IFluidLoadable, IFluidHandle } from '@fluidframework/core-interfaces';
import { strictType } from '@fluidx/utilities';
import { SetAbsolutePathMessage } from '../MessageTypes';

/**
 * Basic IFluidHandle implementation that will only hold the absolutePath
 */
class HostSideBasicFluidHandle implements IFluidHandle {
  public absolutePath = '';

  public get IFluidHandle() {
    return this;
  }

  // This has a dummy value, and does not get updated from inside the iframe
  public readonly isAttached = true;

  async get(): Promise<any> {
    throwNotImplemented();
  }

  public attachGraph(): void {
    throwNotImplemented();
  }
  public bind(): void {
    throwNotImplemented();
  }
}

function throwNotImplemented() {
  throw new Error('Method not implemented');
}

/**
 * Initializes everything needed to handle the IFluidLoadable interface outside the iframe
 * @param sender how to send messages to the inside of the iframe
 * @returns A dummy component with just this interface and a message handler this interface is expecting
 */
export function getHostSideFluidLoadable(): {
  component: IFluidLoadable;
  handlers: PostMessageHandlers<string>;
} {
  const theProxy = new HostSideFluidLoadableProxy();

  const handlers: PostMessageHandlers<string> = {
    ['SetAbsolutePath']: (message) => {
      const path = (message as SetAbsolutePathMessage).payload;
      theProxy.handle.absolutePath = path ? path : '';
    }
  };

  return { component: theProxy, handlers };
}

/**
 * This is a proxy for IFluidLoadable, which only holds a handle
 */
export class HostSideFluidLoadableProxy implements IFluidLoadable {
  constructor() {
    this.innerHandle = new HostSideBasicFluidHandle();
  }

  private innerHandle: IFluidHandle<this>;

  get IFluidLoadable(): IFluidLoadable {
    return this;
  }

  public get handle() {
    return this.innerHandle;
  }
}

/**
 * Sends the absolutePath of the handle from inside the iFrame to the outside
 * @param sender how to send messages to the outside of the iframe
 * @param component the actual component with the real implementation of the interface
 */
export function getGuestSideFluidLoadable(
  component: IFluidObject,
  sender: PostMessageSender
): PostMessageHandlers<string> | undefined {
  const { IFluidLoadable } = component;
  if (!IFluidLoadable) {
    return undefined;
  }

  sender.send(
    strictType<SetAbsolutePathMessage>({
      type: 'SetAbsolutePath',
      payload: IFluidLoadable.handle.absolutePath
    })
  );

  // We do not need to listen to messages from the host side
  return undefined;
}
